/**
 * User and Dealer Tracking Utility for Dealer App
 * Provides consistent user and dealer name tracking across all car management operations
 */

/**
 * Get user and dealer information for tracking
 * @param {Object} currentUser - Current user object
 * @param {Array} userRoles - User roles array
 * @param {string} userRole - Single user role
 * @returns {Object} User and dealer tracking information
 */
export const getUserTrackingInfo = (currentUser, userRoles = [], userRole = null) => {
  if (!currentUser) {
    return {
      userId: null,
      userName: null,
      dealerName: null,
      userRole: null,
      isDealerRole: false
    };
  }

  const userId = currentUser.uid;
  const userName = currentUser.name || currentUser.displayName || currentUser.email;
  
  // Determine if user is a dealer role
  const isDealerRole = userRoles.includes('orguserdealer') || 
                       userRole === 'orguserdealer' || 
                       userRoles.includes('dealer') || 
                       userRole === 'dealer';
  
  // For dealer role users, use their name as dealer name
  // For non-dealer users, use their dealerName field, or fall back to user's email if dealerName is missing
  const dealerName = isDealerRole ? userName : (currentUser.dealerName || currentUser.dealer_name || currentUser.email);
  
  return {
    userId,
    userName,
    dealerName,
    userRole: userRole || userRoles[0] || 'unknown',
    isDealerRole,
    userEmail: currentUser.email,
    organizationId: currentUser.organization
  };
};

/**
 * Create tracking data for car management operations
 * @param {Object} operationData - Operation specific data
 * @param {Object} userInfo - User tracking information
 * @param {string} action - Action being performed
 * @param {string} actionType - Type of action
 * @returns {Object} Complete tracking data
 */
export const createTrackingData = (operationData, userInfo, action, actionType) => {
  return {
    // User tracking
    userId: userInfo.userId,
    userName: userInfo.userName,
    dealerName: userInfo.dealerName,
    userRole: userInfo.userRole,
    isDealerRole: userInfo.isDealerRole,
    userEmail: userInfo.userEmail,
    organizationId: userInfo.organizationId,
    
    // Operation tracking
    action,
    actionType,
    timestamp: new Date().toISOString(),
    
    // Car and operation specific data
    ...operationData
  };
};
